import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

/**
 * ADR-113 — the CONTRACT-addressed lifecycle actions served by the shared
 * ContractActionModals component. The Contracts tab is their authoritative
 * home, and since ADR-158 their ONLY home: no contract verb has a card entry.
 *
 * ADR-149 §3 — what makes a verb belong here: performing it correctly touches
 * exactly ONE contract and leaves the person's others standing. That is the
 * test, and it is about the verb's target, never about which id its payload
 * carries — every verb in this domain is episode-addressed at the event layer
 * (ADR-144 §4.2), so payload shape cannot decide anything.
 *
 * ADR-158 §3 — the test has to be run on the counterfactual where the verb is
 * APPLICABLE. "Hire Withdraw" on a person employed on A and joining on B is
 * not a hire withdrawal (they were never hired for B — B came from Add
 * Contract); on a person joining on two contracts it voids both. So the two
 * joining terminals are person verbs (PERSON_ACTIONS below). Start Delayed
 * stays: two joining contracts may start on different days, so moving one
 * is a complete, correct act on that contract alone.
 *
 * ADR-159 §4 C5 adds VOID_CONTRACT here (named Void Contract, ADR-171) — the contract-level
 * void of one not-yet-started contract while the person keeps another.
 */
export const CONTRACT_ACTIONS = {
  START_DELAYED: 'start-delayed',
  RESIGN_WITHDRAW: 'resign-withdraw',
  CHANGE_END_DATE: 'change-end-date',
  CANCEL_CONTRACT: 'cancel-contract',
  ADD_END_DATE: 'add-end-date',
  CANCEL_SCHEDULED_RENEWAL: 'cancel-scheduled-renewal',
  // ADR-159 §4.1 (C5): void ONE contract that has not started, while the
  // person keeps another. Never the primary.
  VOID_CONTRACT: 'void-contract',
  // ADR-174 §5: set, correct or clear ONE contract's number. Not a lifecycle
  // event — a correction of the contract's identity — but it touches exactly
  // one contract, so it is contract-addressed (ADR-149 §3's test).
  SET_CONTRACT_NUMBER: 'set-contract-number',
};

/**
 * ADR-149 §5.3 — the PERSON-addressed verbs, served by the same modal router.
 *
 * `ContractActionModals` is a modal router, not a semantic claim, so ADR-113
 * §5's one-implementation-per-verb rule survives this split untouched. What
 * could not survive was `CONTRACT_ACTIONS.RESIGN`: a departure closes every
 * contract the person holds, and the constant asserted the opposite of that
 * in the one file whose entire job is the contract-addressed set.
 *
 * ADR-158 §1 — Hire Withdraw and Mark No Show join Resign here for the same
 * reason: withdrawing a hire, or recording that the person never turned up,
 * voids the person's joining — every contract that has not started — and
 * the person becomes Cancelled. The modal bodies said so all along ("the
 * person becomes cancelled … return later through Rehire"). ADR-159 §5.2
 * reads all three as one person verb, End Employment, under three labels.
 *
 * Their homes are the repository card's ⋮ menu and the employee form's
 * person action area (Employment Information tab) — never a contract card
 * (ADR-149 §1 for Resign; ADR-158 §7 for the two joining terminals).
 */
export const PERSON_ACTIONS = {
  RESIGN: 'resign',
  HIRE_WITHDRAW: 'hire-withdraw',
  MARK_NO_SHOW: 'mark-no-show'
};

// ADR-116 — one formatter owns the contract-number label, and the two
// separators mean different things:
//
//   ·  binds the noun to the identifier — `Contract · 10002` is ONE fact
//   —  separates facts ABOUT the contract — type, entity, start date
//
// so `Contract · 10002 — B2B — KiperTech (Poland)` stays parseable: a reader
// can see where the contract's identity ends and its attributes begin.
//
// `#` is deliberately NOT used. In this codebase `#` marks an ordinal radar
// itself issues (`Amendment #3`, employmentEventAnnotations.js:95), and the
// change log interleaves both kinds of row in one column (ADR-112 §11), so the
// sigil is the reader's only cue for which is which. A contract number is the
// opposite kind of value: a reference carried from a system of record radar
// does not own, and free text by ADR-137 (`UZ/2026/0143`, `0001021`), which a
// numeric sigil misrepresents. See ADR-116 §3.1-3.2.
export const CONTRACT_FACT_SEPARATOR = ' — ';

/**
 * The contract's identity token: `Contract · 10002`.
 *
 * Returns null when there is no number, so callers fall back rather than
 * render a bare noun — ADR-115 §4: type, entity and start date are more
 * distinguishing than the word "Contract" on its own.
 *
 * NOT for prose, and NOT for persisted text. The change log embeds the number
 * in a sentence that already uses an em dash, and the fixed-term episode
 * comment is stored and later matched by exact reconstruction (ADR-146 §4.1).
 * Both are exempt by design — ADR-116 §3.3.
 */
export const formatContractNumber = (number) => {
  const value = number === undefined || number === null ? '' : String(number).trim();
  return value === '' ? null : `Contract · ${value}`;
};

/**
 * ADR-174 §3.2 — a company named with its country: `KiperTech (Poland)` (the
 * form ADR-116 already used). The country belongs to the company fact, so it
 * is parenthesised rather than becoming another ` — ` segment. With no country
 * loaded the name stands alone — never `()`.
 *
 * @param {{ name?: string, country?: { name?: string } | null } | null} company
 * @returns {string|null}
 */
export const formatCompanyWithCountry = (company) => {
  const name = company?.name ? String(company.name).trim() : '';
  if (!name) return null;
  const country = company?.country?.name ? String(company.country.name).trim() : '';
  return country ? `${name} (${country})` : name;
};

/**
 * ADR-113 §5 / ADR-142 §11 — the contract's identity: its number when it has
 * one, plus type and entity; never an ordinal. Used for modal titles and, since
 * ADR-148, for every row of the Employment Episodes accordion.
 *
 * Returns '' when nothing is known about the contract — callers supply their own
 * terminal fallback (`|| 'Contract'`), as ContractDetails already did.
 *
 * @param {Object|null} employee  the card's flat payload (§5.5-sourced: the open
 *   episode, else the latest). Pass null on a history surface — see below.
 * @param {Object|null} episode   the episode being labelled.
 * @param {Object}  [options]
 * @param {boolean} [options.withStartDate=true]  append `from DD/MM/YYYY` to the
 *   no-number form. Default preserves ADR-113's modal titles unchanged; pass
 *   false on a surface that renders the date range itself (ADR-148 §3.2), or the
 *   start date prints twice in two formats on the row.
 */
export const contractIdentityLabel = (employee, episode = null, { withStartDate = true } = {}) => {
  // ADR-187 D3 — the person's flat fields describe the PRIMARY (ADR-156 §2):
  // they may stand in only for the primary itself (or when no episode, or no
  // primary pointer, is known). A secondary without a number must not be
  // labelled with the primary's number, type or company.
  const primaryId = employee?.primaryEmploymentEpisodeId || employee?.currentEmploymentEpisodeId || null;
  const person = !episode?.id || !primaryId || episode.id === primaryId ? employee : null;
  const ref = episode?.payload?.sourceReference;
  const number = ref?.contractId || person?.currentEpisodeContractNumber || null;
  const start = episode?.startDate || person?.contractStartDate || null;

  // ADR-148 §3.2 — the EPISODE'S OWN terms win. On the Contracts tab the two
  // sources agree (the employee's flat fields describe the very contract that
  // tab shows), so this is a no-op there. On the Episodes tab, which lists
  // history, the employee payload describes a DIFFERENT contract — labelling a
  // 2019 episode with today's type and entity would invent history, which is
  // exactly what ADR-142 §5A.9 step 2 refused to do in the backfill. Episode
  // relations arrive from the ADR-148 include on GET /employment/episodes.
  const typeName = episode?.contractType?.name || person?.contractType?.name || null;
  // ADR-174 §3.2 — the company states its country: `Software Mind S.R.L.
  // (Moldova)` and `Software Mind CF S.R.L. (Romania)` are different
  // employers, and the name alone does not say so.
  const company = episode?.contractCompany?.name ? episode.contractCompany
    : (person?.contractCompany?.name ? person.contractCompany : null);
  const companyName = formatCompanyWithCountry(company);

  const parts = [];
  const identity = formatContractNumber(number);
  if (identity) {
    parts.push(identity);
  }
  if (typeName) {
    parts.push(typeName);
  }
  if (companyName) {
    parts.push(companyName);
  }
  if (withStartDate && !number && start) {
    parts.push(`from ${dayjs.utc(start).format('DD/MM/YYYY')}`);
  }
  return parts.join(CONTRACT_FACT_SEPARATOR);
};
